import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { WikiDocumentStatus } from '@innermate/shared';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Archive, ArchiveRestore, Eye, RefreshCw, RotateCcw, Search } from 'lucide-react';
import { useEffect, useState } from 'react';

const STATUS_LABEL: Record<WikiDocumentStatus, string> = {
  uploaded: '업로드됨',
  parsing: '파싱 중',
  parsed: '파싱 완료',
  digesting: '위키 정리 중',
  done: '완료',
  failed: '실패',
  deleted: '외부 삭제됨',
  archived: '보관됨',
};

const STATUS_TONE: Record<WikiDocumentStatus, string> = {
  uploaded: 'bg-foreground/10 text-foreground-muted',
  parsing: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  parsed: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  digesting: 'bg-accent/10 text-accent',
  done: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  failed: 'bg-destructive/10 text-destructive',
  deleted: 'bg-foreground/10 text-foreground-faint line-through',
  archived: 'bg-foreground/10 text-foreground-muted',
};

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function isInProgress(s: WikiDocumentStatus) {
  return s === 'uploaded' || s === 'parsing' || s === 'parsed' || s === 'digesting';
}

export function AdminWikiDocuments() {
  const queryClient = useQueryClient();

  const PAGE_SIZE = 100;
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'archived'>('all');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const docsQuery = useInfiniteQuery({
    queryKey: ['admin', 'wiki', 'documents', debouncedQ, statusFilter],
    queryFn: ({ pageParam }) =>
      api.adminWikiDocuments({
        offset: pageParam,
        limit: PAGE_SIZE,
        q: debouncedQ || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
      }),
    initialPageParam: 0,
    getNextPageParam: (_lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.documents.length, 0);
      const total = allPages[0]?.total ?? 0;
      return loaded < total ? loaded : null;
    },
    refetchInterval: (qs) => {
      const docs = qs.state.data?.pages.flatMap((p) => p.documents) ?? [];
      return docs.some((d) => isInProgress(d.status)) ? 5_000 : false;
    },
  });

  const reprocessMutation = useMutation({
    mutationFn: (id: string) => api.wikiDocumentReprocess(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'documents'] }),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.wikiDocumentDelete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'documents'] }),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => api.wikiDocumentRestore(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'documents'] }),
  });

  const docs = docsQuery.data?.pages.flatMap((p) => p.documents) ?? [];
  const totalCount = docsQuery.data?.pages[0]?.total ?? docs.length;
  const inProgressDocs = docs.filter((d) => isInProgress(d.status));

  return (
    <PageShell
      header={
        <PageHeader
          title="위키 자료 (전체)"
          count={totalCount}
          description="모든 사용자가 업로드한 원본 자료. 실패/오래된 자료 재처리·삭제."
        />
      }
    >
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-sm font-semibold text-foreground-muted">
          {debouncedQ
            ? `검색 결과 ${totalCount}`
            : statusFilter === 'archived'
              ? `보관된 자료 ${totalCount}`
              : statusFilter === 'active'
                ? `활성 자료 ${totalCount}`
                : `전체 자료 ${totalCount}`}
        </h2>
        <div className="ml-auto flex items-center gap-2">
          <div className="inline-flex rounded-md border border-border bg-surface p-0.5 text-xs">
            {(
              [
                ['all', '전체'],
                ['active', '활성'],
                ['archived', '보관됨'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setStatusFilter(value)}
                className={cn(
                  'rounded px-2.5 py-1 font-medium transition-colors',
                  statusFilter === value
                    ? 'bg-accent text-accent-foreground'
                    : 'text-foreground-muted hover:text-foreground',
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted pointer-events-none" />
            <input
              type="text"
              placeholder="제목 또는 업로더 검색"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-9 w-56 pl-9 pr-3 text-sm rounded-md border border-border bg-surface focus:outline-none focus:border-accent"
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="h-9"
            onClick={() => docsQuery.refetch()}
            disabled={docsQuery.isFetching}
          >
            <RefreshCw className={cn('size-4', docsQuery.isFetching && 'animate-spin')} />
            새로고침
          </Button>
        </div>
      </div>

      {docsQuery.isPending ? (
        <p className="text-sm text-foreground-muted">로딩 중…</p>
      ) : docs.length === 0 ? (
        <div className="rounded-md border border-border bg-surface p-6 text-center text-foreground-muted">
          {statusFilter === 'archived'
            ? '보관된 자료가 없습니다.'
            : statusFilter === 'active'
              ? '활성 자료가 없습니다.'
              : '업로드된 자료가 없습니다.'}
        </div>
      ) : (
        <div className="space-y-6">
          {inProgressDocs.length > 0 ? (
            <section>
              <h2 className="mb-3 text-sm font-semibold text-foreground-muted">
                처리 중 — 5초마다 자동 갱신
              </h2>
              <ul className="space-y-2">
                {inProgressDocs.map((d) => (
                  <li key={d.id} className="rounded-md border border-border bg-surface p-3">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-medium truncate">{d.originalName}</span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium shrink-0 ${STATUS_TONE[d.status]}`}
                      >
                        {STATUS_LABEL[d.status]}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-3 text-xs text-foreground-faint">
                      <span>업로더: {d.uploaderName ?? '(?)'}</span>
                      {d.categorySlug ? <span>{d.categorySlug}</span> : null}
                    </div>
                    <div className="mt-2 text-xs text-foreground-muted">
                      {d.status === 'uploaded' && '워커가 처리를 시작합니다…'}
                      {d.status === 'parsing' && '파일을 마크다운으로 변환 중…'}
                      {d.status === 'parsed' && '변환 완료, LLM이 위키 정리를 시작합니다…'}
                      {d.status === 'digesting' &&
                        (d.digestSummary?.totalChunks
                          ? `LLM 청크 처리 중 — ${d.digestSummary.processedChunks ?? 0}/${d.digestSummary.totalChunks} 청크${d.digestSummary.currentChunkHeading ? ` · ${d.digestSummary.currentChunkHeading}` : ''}`
                          : 'LLM이 위키 정리 중…')}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <section>
            <ul className="space-y-2">
              {docs.map((d) => (
                <li
                  key={d.id}
                  className={`rounded-md border bg-surface p-3 ${d.status === 'deleted' || d.status === 'archived' ? 'border-border opacity-60' : 'border-border'}`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    {d.status === 'deleted' ? (
                      <span className="font-medium text-foreground-muted line-through">
                        {d.originalName}
                      </span>
                    ) : d.status === 'archived' ? (
                      <span className="font-medium text-foreground-muted">{d.originalName}</span>
                    ) : (
                      <a
                        href={api.wikiDocumentDownloadUrl(d.id)}
                        className="font-medium hover:text-accent"
                      >
                        {d.originalName}
                      </a>
                    )}
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[d.status]}`}
                    >
                      {STATUS_LABEL[d.status]}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-3 text-xs text-foreground-faint">
                    <span>업로더: {d.uploaderName ?? '(?)'}</span>
                    <span>.{d.ext}</span>
                    <span>{formatBytes(d.sizeBytes)}</span>
                    {d.categorySlug ? (
                      <span>카테고리: {d.categorySlug}</span>
                    ) : (
                      <span>(미분류)</span>
                    )}
                    <span>{new Date(d.createdAt).toLocaleString('ko-KR')}</span>
                  </div>
                  {d.status === 'failed' && d.error ? (
                    <div className="mt-2 rounded bg-destructive/10 px-2 py-1 text-xs text-destructive">
                      오류: {d.error}
                    </div>
                  ) : null}
                  {d.status === 'done' && d.digestSummary?.pages.length ? (
                    <div className="mt-2 text-xs text-foreground-muted">
                      생성/갱신:{' '}
                      {d.digestSummary.pages.map((p, i) => (
                        <span key={p.slug}>
                          {i > 0 ? ', ' : ''}
                          <Link
                            to="/wikis/p/$slug"
                            params={{ slug: p.slug }}
                            className="text-accent hover:underline"
                          >
                            {p.slug}
                          </Link>
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {d.status !== 'deleted' && d.status !== 'archived' ? (
                    <div className="mt-2 flex gap-2">
                      <Link
                        to="/admin/wikis/documents/$id/preview"
                        params={{ id: d.id }}
                        className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:border-accent"
                      >
                        <Eye className="h-3 w-3" />
                        변환 결과
                      </Link>
                      <button
                        type="button"
                        onClick={() => reprocessMutation.mutate(d.id)}
                        disabled={
                          reprocessMutation.isPending ||
                          d.status === 'parsing' ||
                          d.status === 'digesting'
                        }
                        className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:border-accent disabled:opacity-50"
                      >
                        <RotateCcw className="h-3 w-3" />
                        재처리
                      </button>
                      <button
                        type="button"
                        onClick={() => archiveMutation.mutate(d.id)}
                        className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:border-foreground-muted text-foreground-muted"
                      >
                        <Archive className="h-3 w-3" />
                        보관
                      </button>
                    </div>
                  ) : null}
                  {d.status === 'archived' ? (
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        onClick={() => restoreMutation.mutate(d.id)}
                        className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:border-accent text-foreground-muted"
                      >
                        <ArchiveRestore className="h-3 w-3" />
                        보관 해제
                      </button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
            {docsQuery.hasNextPage ? (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => docsQuery.fetchNextPage()}
                  disabled={docsQuery.isFetchingNextPage}
                >
                  {docsQuery.isFetchingNextPage
                    ? '불러오는 중…'
                    : `더 보기 (${docs.length}/${totalCount})`}
                </Button>
              </div>
            ) : null}
          </section>
        </div>
      )}
    </PageShell>
  );
}
